'use client';

import { useEffect, useRef, useState } from 'react';

// 百度地图类型声明
interface BMapType {
  Map: new (container: HTMLElement) => any;
  Point: new (lng: number, lat: number) => any;
  NavigationControl: new (opts?: any) => any;
  ScaleControl: new (opts?: any) => any;
  OverviewMapControl: new (opts?: any) => any;
  Marker: new (point: any) => any;
  InfoWindow: new (content: string, opts?: any) => any;
}

// 百度地图常量声明
interface BMapConstants {
  ANCHOR_TOP_LEFT: any;
  ANCHOR_BOTTOM_LEFT: any;
  ANCHOR_BOTTOM_RIGHT: any;
  NAVIGATION_CONTROL_LARGE: any;
  UNIT_IMPERIAL: any;
}

interface BaiduMapProps {
  apiKey: string;
  center: { lng: number; lat: number };
  zoom?: number;
  className?: string;
  companyAddress?: string;
}

export default function BaiduMap({ apiKey, center, zoom = 15, className = '', companyAddress = '' }: BaiduMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [retryCount, setRetryCount] = useState(0);

  // 检查API密钥是否有效
  const isValidApiKey = apiKey && apiKey !== 'demo_key' && apiKey.length > 10;
  
  // 生成静态地图图片URL作为备选方案
  const staticMapUrl = `https://api.map.baidu.com/staticimage/v2?ak=${apiKey}&center=${center.lng},${center.lat}&width=800&height=400&zoom=${zoom}&markers=${center.lng},${center.lat}`;

  useEffect(() => {
    if (!mapRef.current) return;

    setLoading(true);
    setError('');

    // 检查是否已经加载了百度地图API
    if ((window as any).BMap) {
      initializeMap();
    } else {
      // 检查是否正在加载中，避免重复加载
      if ((window as any).baiduMapLoading) {
        return;
      }
      
      (window as any).baiduMapLoading = true;
      
      // 动态加载百度地图API v3.0版本（使用JSONP方式避免跨域问题）
      const script = document.createElement('script');
      script.src = `https://api.map.baidu.com/api?v=3.0&ak=${apiKey}&callback=baiduMapInit`;
      script.async = true;
      script.defer = false;
      
      // 设置全局回调函数
      (window as any).baiduMapInit = () => {
        (window as any).baiduMapLoading = false;
        initializeMap();
      };
      
      // 添加错误处理
      script.onerror = () => {
        setLoading(false);
        setError('百度地图API加载失败，请检查网络连接或API密钥');
        console.error('百度地图API加载失败');
        (window as any).baiduMapLoading = false;
        // 清理回调函数
        (window as any).baiduMapInit = null;
      };

      document.head.appendChild(script);

      return () => {
        // 清理函数
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
        (window as any).baiduMapLoading = false;
      };
    }
  }, [apiKey, center.lng, center.lat, zoom, retryCount]);

  const initializeMap = () => {
    if (!mapRef.current || !(window as any).BMap) return;

    const BMap = (window as any).BMap as BMapType;
    const BMAP = (window as any) as BMapConstants;
    
    // 创建地图实例
    const map = new BMap.Map(mapRef.current);
    const point = new BMap.Point(center.lng, center.lat);
    
    // 初始化地图
    map.centerAndZoom(point, zoom);
    
    // 设置地图事件
    map.enableScrollWheelZoom();
    map.enableKeyboard();
    map.enableDragging();
    map.enableDoubleClickZoom();
    
    // 向地图添加控件（按照您提供的模板配置）
    // 比例尺控件
    const scaleControl = new BMap.ScaleControl({anchor: BMAP.ANCHOR_BOTTOM_LEFT});
    scaleControl.setUnit(BMAP.UNIT_IMPERIAL);
    map.addControl(scaleControl);
    
    // 导航控件
    const navControl = new BMap.NavigationControl({
      anchor: BMAP.ANCHOR_TOP_LEFT,
      type: BMAP.NAVIGATION_CONTROL_LARGE
    });
    map.addControl(navControl);
    
    // 缩略图控件
    const overviewControl = new BMap.OverviewMapControl({
      anchor: BMAP.ANCHOR_BOTTOM_RIGHT,
      isOpen: true
    });
    map.addControl(overviewControl);

    // 创建标记
    const marker = new BMap.Marker(point);
    map.addOverlay(marker);

    // 创建信息窗口
    const infoWindow = new BMap.InfoWindow(
      `<div style="padding: 10px;">
        <h3 style="margin: 0 0 5px 0; font-size: 14px; color: #333;">公司位置</h3>
        <p style="margin: 0; font-size: 12px; color: #666;">${companyAddress || '公司地址'}</p>
        <p style="margin: 5px 0 0 0; font-size: 11px; color: #999;">经度: ${center.lng}<br>纬度: ${center.lat}</p>
      </div>`,
      {
        width: 250,
        height: 100
      }
    );

    // 添加点击事件打开信息窗口
    marker.addEventListener('click', () => {
      map.openInfoWindow(infoWindow, point);
    });

    // 保存地图实例引用
    mapInstanceRef.current = map;
    
    setLoading(false);
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  // 如果没有有效的API密钥，显示配置提示
  if (!isValidApiKey) {
    return (
      <div className={`relative w-full h-full rounded-2xl overflow-hidden ${className}`} style={{ minHeight: '400px' }}>
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900 z-10">
          <div className="text-center p-6 max-w-md">
            <div className="text-yellow-400 text-5xl mb-4">🗺️</div>
            <h3 className="text-white font-bold text-lg mb-2">地图配置提示</h3>
            <p className="text-gray-300 text-sm mb-4">
              请配置有效的百度地图API密钥以显示地图。
              您需要在环境变量中设置 <code className="bg-gray-700 px-1 rounded">NEXT_PUBLIC_BAIDU_MAP_API_KEY</code>
            </p>
            <div className="bg-gray-800/50 rounded-lg p-3 text-xs text-gray-400">
              <p>当前坐标：经度 {center.lng}，纬度 {center.lat}</p>
              <p className="mt-1">公司地址：{companyAddress || '未设置'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative w-full h-full rounded-2xl overflow-hidden ${className}`} style={{ minHeight: '400px' }}>
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-gray-600">地图加载中...</p>
          </div>
        </div>
      )}
      
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="text-center p-6">
            <div className="text-red-500 text-4xl mb-2">⚠️</div>
            <p className="text-red-600 mb-2">{error}</p>
            <button 
              onClick={handleRetry}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              重试加载
            </button>
          </div>
        </div>
      )}
      
      <div 
        ref={mapRef} 
        className="w-full h-full"
      />
    </div>
  );
}

// 扩展Window接口以包含BMap类型
declare global {
  interface Window {
    BMap: any;
  }
}